<template>
    <hts-modal
        :show="visible"
        size="large"
        :m-type="!disabled ? 'confirm' : 'modal'"
        :title="$t(`labels.nodeModule.${type}`)"
        @confirm="saveWorkingTime"
        @hide="handleClose">
        <template #custom>
            <div class="mt-5" />
            <br >
            <div class="grid grid-cols-5 text-center mb-3">
                <h4>
                    {{ $t('labels.timeFrom') }}
                </h4>
                <p />
                <h4>
                    {{ $t('labels.timeTo') }}
                </h4>
            </div>

            <div class="grid grid-cols-5">
                <div class="flex items-center gap-4">
                    <hts-form-select
                        v-model="times.start.hour"
                        :items="hours"
                        key-id="value"
                        key-label="label"
                        name="startHour"
                        prop="startHour"
                        :clearable="false"
                        :readonly="disabled" />
                    <span>
                        :
                    </span>
                    <hts-form-select
                        v-model="times.start.minute"
                        :items="minutes"
                        key-id="value"
                        key-label="label"
                        name="startMinute"
                        prop="startMinute"
                        :clearable="false"
                        :readonly="disabled" />
                </div>
                <div>
                    <p class="text-center mt-3">
                        -
                    </p>
                </div>
                <div class="flex items-center gap-4">
                    <hts-form-select
                        v-model="times.end.hour"
                        :items="hours"
                        key-id="value"
                        key-label="label"
                        name="endHour"
                        prop="endHour"
                        :clearable="false"
                        :readonly="disabled" />
                    <span>
                        :
                    </span>
                    <hts-form-select
                        v-model="times.end.minute"
                        :items="minutes"
                        key-id="value"
                        key-label="label"
                        name="endMinute"
                        prop="endMinute"
                        :clearable="false"
                        :readonly="disabled" />
                </div>
                <hts-form-checkbox
                    v-if="!disabled"
                    v-model="workingAllDay"
                    :items="[{ label: $t('labels.workingAllDay'), value: true }]"
                    class="ml-10"
                    data-attribute="check-box"
                    @change="changeWorkingAllDay" />
            </div>
            <div>
                <div class="my-3">
                    <hts-form-button
                        v-if="!disabled"
                        color="success"
                        size="small"
                        @click="handleSetAll">
                        {{ $t('labels.setToAll') }}
                    </hts-form-button>
                </div>
                <!-- Set for alls day -->
                <div
                    v-for="(date, index) in weeks"
                    :key="date"
                    class="grid grid-cols-8 items-center bg-default-100 dark:bg-default-600 hover:bg-default-400 dark:hover:bg-default-500 p-3 rounded-md mt-2">
                    <span class="font-bold">
                        {{ $t(`labels.${date}`) }}
                    </span>
                    <div class="text-center col-span-6 text-sm flex gap-4">
                        <div class="w-full flex flex-wrap items-center gap-4">
                            <div
                                v-for="(time, indexT) in getTimeTypeInDate(date, 'inTime')"
                                :key="time"
                                class="px-2 py-1 rounded-lg flex gap-2"
                                :class="{
                                    'bg-success-400': time.type === 'inTime',
                                    'bg-info-400': time.type === 'overTime',
                                    'bg-warning-400': time.type === 'weekend'
                                }">
                                <p class="h-5 leading-5 p-0 m-0 text-white">
                                    {{ convertTimeFormat(time.start) }} - {{ convertTimeFormat(time.end) }}
                                </p>
                                <hi-x-circle-icon
                                    v-if="workingTimes?.[date] && !disabled"
                                    class="h-5 w-5 text-error-500 cursor-pointer"
                                    @click="(e) => deleteWorkingTime(e, date, time.id)" />
                            </div>
                        </div>
                        <div
                            v-if="getTimeTypeInDate(date, 'overTime')?.length"
                            class="w-full flex flex-wrap items-center gap-4">
                            <div
                                v-for="(time, indexT) in getTimeTypeInDate(date, 'overTime')"
                                :key="time"
                                class="px-2 py-1 rounded-lg flex gap-2"
                                :class="{
                                    'bg-success-400': time.type === 'inTime',
                                    'bg-info-400': time.type === 'overTime',
                                    'bg-warning-400': time.type === 'weekend'
                                }">
                                <p class="h-5 leading-5 p-0 m-0 text-white">
                                    {{ convertTimeFormat(time.start) }} - {{ convertTimeFormat(time.end) }}
                                </p>
                                <hi-x-circle-icon
                                    v-if="workingTimes?.[date] && !disabled"
                                    class="h-5 w-5 text-error-500 cursor-pointer"
                                    @click="(e) => deleteWorkingTime(e, date, time.id)" />
                            </div>
                        </div>
                    </div>
                    <div class="text-right flex justify-end">
                        <hi-clock-icon
                            v-if="!disabled"
                            class="h-7 w-7 text-main-600 cursor-pointer ml-4"
                            @click="setWorkingTime(date)" />
                    </div>
                </div>
            </div>
        </template>
    </hts-modal>
</template>
<script setup lang="ts">
import { isRef } from '@vue/reactivity'
import { $lodash } from '#imports'
const { $disableReactive } = useNuxtApp()

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: Number,
    default: 0
  },
  sizeItemInput: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 1
  },
  customStyle: {
    type: String,
    default: ''
  },
  itemIsSelectedToView: {
    type: Object,
    default: () => {}
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const type = ref('inTime')
const emits = defineEmits(['update', 'close'])
const weeks = ref(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])
const workingAllDay = ref(false)

const visible = ref(false)

const times = ref({
  start: {
    hour: '8',
    minute: '30'
  },
  end: {
    hour: '18',
    minute: '0'
  }
} as any)

const workingTimes = ref<{ [key: string]: any }>({
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: []
})

watch(
  () => times.value,
  () => {
    if (times.value.start.hour === '0' && times.value.end.hour === '0' && times.value.start.minute === '0' && times.value.end.minute === '0') {
      workingAllDay.value = true
      return
    }
    workingAllDay.value = false
  },
  { deep: true }
)

const getTimeTypeInDate = (date: string, type: string) => {
  return workingTimes.value[date].filter((item: any) => item.type === type) || []
}

const changeWorkingAllDay = () => {
  if (!workingAllDay.value) {
    times.value = {
      start: {
        hour: '8',
        minute: '30'
      },
      end: {
        hour: '18',
        minute: '0'
      }
    }
    return
  }

  times.value = {
    start: {
      hour: '0',
      minute: '0'
    },
    end: {
      hour: '0',
      minute: '0'
    }
  }
}

const setWorkingTime = (date: string) => {
  const { start, end } = times.value
  workingTimes?.value[date]?.push({
    start: getTimeInMinutes(start),
    end: getTimeInMinutes(end),
    type: type.value,
    id: Math.random().toString(36).substring(7)
  })
}

const handleSetAll = () => {
  weeks.value.forEach((date: string) => {
    setWorkingTime(date)
  })
}

const deleteWorkingTime = (e: any, day: string, id: string) => {
  try {
    e.preventDefault()
    e.stopPropagation()
    if (workingTimes.value[day] && workingTimes.value[day].length) {
      workingTimes.value[day] = workingTimes.value[day].filter((item: any) => item.id !== id)
    }
  } catch (e) {}
}

const open = (workingTime: any, typeT: string) => {
  workingTimes.value = $disableReactive(workingTime)
  type.value = typeT
  visible.value = true
  times.value = {
    start: {
      hour: '8',
      minute: '30'
    },
    end: {
      hour: '18',
      minute: '0'
    }
  }
}

const getLabel = (key: string) => {
  if (key === 'inTime') {
    return $t('labels.nodeModule.inTime')
  }
  if (key === 'overTime') {
    return $t('labels.nodeModule.overTime')
  }
  if (key === 'weekend') {
    return $t('labels.nodeModule.weekend')
  }
}
const saveWorkingTime = () => {
  try {
    const checkW: boolean = checkWorkingTimeOverlap()
    if (checkW !== true) {
      process.client && $toast().error(String(checkW))
      return
    }

    emits('update', $disableReactive(workingTimes.value))
    visible.value = false
    times.value = {
      start: {
        hour: '8',
        minute: '30'
      },
      end: {
        hour: '18',
        minute: '0'
      }
    }
  } catch (e: any) {

  }
}

const checkWorkingTimeOverlap = () => {
  try {
    const changes: {
      key: string
      value: number
    }[] = []

    for (const day in workingTimes.value) {
      const dayTimeSlots = $disableReactive(workingTimes.value[day])

      for (let i = 0; i < Object.keys(dayTimeSlots)?.length; i++) {
        const timeSlotA = $disableReactive(dayTimeSlots[i])

        if (timeSlotA.end === 0) {
          timeSlotA.end = 1440
          changes.push({
            key: `${day}.${i}.end`,
            value: 1440
          })
        }

        if (timeSlotA.start >= timeSlotA.end && timeSlotA.end !== 0) {
          throw new Error($t('labels.timeToGreaterThanTimeFrom'))
        }

        for (let j = i + 1; j < Object.keys(dayTimeSlots)?.length; j++) {
          const timeSlotB = $disableReactive(dayTimeSlots[j])

          if (timeSlotB.end === 0) {
            timeSlotB.end = 1440
            changes.push({
              key: `${day}.${j}.end`,
              value: 1440
            })
          }

          if (timeSlotB.start >= timeSlotB.end) {
            throw new Error($t('labels.timeToGreaterThanTimeFrom'))
          }
          if (
            (timeSlotA.start >= timeSlotB.start && timeSlotA.start < timeSlotB.end) ||
            (timeSlotA.end > timeSlotB.start && timeSlotA.end <= timeSlotB.end) ||
            (timeSlotB.start >= timeSlotA.start && timeSlotB.start < timeSlotA.end) ||
            (timeSlotB.end > timeSlotA.start && timeSlotB.end <= timeSlotA.end)
          ) {
            throw new Error($t('labels.workingTimeDoNotOverlap'))
          }
        }
      }
    }

    changes.forEach((item: { [key: string]: any }) => {
      $lodash.set(workingTimes.value, item.key, item.value)
    })

    return true
  } catch (e: any) {
    return e.message || ''
  }
}

const getTimeInMinutes = (time: any) => {
  const hour = Number(time?.hour)
  const minute = Number(time?.minute)
  return hour * 60 + minute
}

const convertTimeFormat = (minuteInDay: number) => {
  try {
    if (minuteInDay === 1440) {
      return '00:00'
    }
    const hour = Math.floor(minuteInDay / 60)
    const minute = minuteInDay % 60

    const formattedHour = hour.toString().padStart(2, '0')
    const formattedMinute = minute.toString().padStart(2, '0')

    return `${formattedHour}:${formattedMinute}`
  } catch (e) {
    return ''
  }
}

const handleClose = () => {
  visible.value = false
}

defineExpose({ open })

const hours = [
  { value: '0', label: '00' },
  { value: '1', label: '01' },
  { value: '2', label: '02' },
  { value: '3', label: '03' },
  { value: '4', label: '04' },
  { value: '5', label: '05' },
  { value: '6', label: '06' },
  { value: '7', label: '07' },
  { value: '8', label: '08' },
  { value: '9', label: '09' },
  { value: '10', label: '10' },
  { value: '11', label: '11' },
  { value: '12', label: '12' },
  { value: '13', label: '13' },
  { value: '14', label: '14' },
  { value: '15', label: '15' },
  { value: '16', label: '16' },
  { value: '17', label: '17' },
  { value: '18', label: '18' },
  { value: '19', label: '19' },
  { value: '20', label: '20' },
  { value: '21', label: '21' },
  { value: '22', label: '22' },
  { value: '23', label: '23' }
]
const minutes = [
  { value: '0', label: '00' },
  { value: '1', label: '01' },
  { value: '2', label: '02' },
  { value: '3', label: '03' },
  { value: '4', label: '04' },
  { value: '5', label: '05' },
  { value: '6', label: '06' },
  { value: '7', label: '07' },
  { value: '8', label: '08' },
  { value: '9', label: '09' },
  { value: '10', label: '10' },
  { value: '11', label: '11' },
  { value: '12', label: '12' },
  { value: '13', label: '13' },
  { value: '14', label: '14' },
  { value: '15', label: '15' },
  { value: '16', label: '16' },
  { value: '17', label: '17' },
  { value: '18', label: '18' },
  { value: '19', label: '19' },
  { value: '20', label: '20' },
  { value: '21', label: '21' },
  { value: '22', label: '22' },
  { value: '23', label: '23' },
  { value: '24', label: '24' },
  { value: '25', label: '25' },
  { value: '26', label: '26' },
  { value: '27', label: '27' },
  { value: '28', label: '28' },
  { value: '29', label: '29' },
  { value: '30', label: '30' },
  { value: '31', label: '31' },
  { value: '32', label: '32' },
  { value: '33', label: '33' },
  { value: '34', label: '34' },
  { value: '35', label: '35' },
  { value: '36', label: '36' },
  { value: '37', label: '37' },
  { value: '38', label: '38' },
  { value: '39', label: '39' },
  { value: '40', label: '40' },
  { value: '41', label: '41' },
  { value: '42', label: '42' },
  { value: '43', label: '43' },
  { value: '44', label: '44' },
  { value: '45', label: '45' },
  { value: '46', label: '46' },
  { value: '47', label: '47' },
  { value: '48', label: '48' },
  { value: '49', label: '49' },
  { value: '50', label: '50' },
  { value: '51', label: '51' },
  { value: '52', label: '52' },
  { value: '53', label: '53' },
  { value: '54', label: '54' },
  { value: '55', label: '55' },
  { value: '56', label: '56' },
  { value: '57', label: '57' },
  { value: '58', label: '58' },
  { value: '59', label: '59' }
]
</script>
